/**
 * @documental
 * @export Field
 * @name
 * NumberTextbox.Field
 */
import { FC, useEffect, useMemo } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { FieldBaseProps } from "../FieldBaseProps";
import { useInputDisabled } from "../InputGroup/useInputDisabled";
import { useInputReadonly } from "../InputGroup/useInputReadonly";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { DisabledRequiredRule, required as requiredRule } from "../validation/rules";
import { Input } from "./Input";
import { SharedProps } from "./SharedProps";

/**
 * The `Field` component properties.
 */

export interface FieldProps extends Omit<FieldBaseProps<number | null>, "autocomplete">, SharedProps {
  /**
   * indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * the step attribute for the number input, which specifies the legal number intervals for the input.
   * @default 1
   */
  readonly step?: number | "any";

  /**
   * the minimum value allowed for the number input.
   */
  readonly min?: number;

  /**
   * the maximum value allowed for the number input.
   */
  readonly max?: number;

  /**
   * The input mode hint for virtual keyboards.
   * @default "numeric"
   */
  readonly inputMode?: "numeric" | "decimal";

  /**
   * Callback fired when the Enter key is pressed
   */
  readonly onEnter?: (value: number | null) => void;
}

/**
 * A React Hook Form compatible NumberTextbox Field component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, required = false, defaultValue = null, validator, min, max, step, inputMode, onEnter, ...restProps } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const rules = useMemo(
    () => ({
      ...(disabled || readOnly ? DisabledRequiredRule : requiredRule(required)),
      validate:
        disabled || readOnly
          ? undefined
          : (value: number | null, formValues: FieldValues) => {
              if (value === null || value === undefined || typeof value !== "number" || isNaN(value)) return true;

              if (min !== undefined && value < min) return `Cannot be less than ${min}`;
              if (max !== undefined && value > max) return `Cannot be greater than ${max}`;

              return validator ? validator(value, formValues) : true;
            },
    }),
    [disabled, readOnly, required, validator, min, max],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          ref={ref}
          value={value}
          required={required}
          defaultValue={defaultValue}
          disabled={disabled}
          readOnly={readOnly}
          min={min}
          max={max}
          step={step}
          inputMode={inputMode}
          onChange={(newValue) => onChange(newValue)}
          onFocusExit={onBlur}
          onEnter={onEnter}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
